import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

interface GoalCompletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  goalName: string;
  isLoading: boolean;
  isCompleted?: boolean;
}

export default function GoalCompletionDialog({
  open,
  onOpenChange,
  onConfirm,
  goalName,
  isLoading,
  isCompleted = false
}: GoalCompletionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCompleted ? (
              <>
                <XCircle className="h-5 w-5 text-amber-500" />
                Mark as Incomplete
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                Complete Goal
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isCompleted
              ? "Are you sure you want to mark this goal as incomplete?"
              : "Are you sure you want to mark this goal as complete?"}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-center font-medium text-lg">{goalName}</p>
          <p className="text-center text-sm text-muted-foreground mt-2">
            {isCompleted
              ? "This will change the goal back to active status"
              : "This will record this goal as accomplished"}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            className={isCompleted
              ? "bg-amber-600 hover:bg-amber-700"
              : "bg-green-600 hover:bg-green-700"}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isCompleted ? "Processing..." : "Completing..."}
              </>
            ) : (
              isCompleted ? "Mark as Incomplete" : "Mark as Complete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}